/* eslint-disable tailwindcss/no-custom-classname */
import React, { ChangeEvent, FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { useParams } from 'react-router-dom';
import { Modal, Paper } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { DatePicker, DualButton, RhfDropdown, RhfTextField } from '@components';
import { translate, translateDynamic } from '@utils/locale';
import { colors } from '@constants/colors';
import { ToastTypes } from '@type/toast';
import {
  ALLOCATION_MAX_AMOUNT,
  CONVERTIBLE_TERMS_MAX_LENGTH,
  ValuationTypes
} from '@constants/common';
import { getNumbersOnly } from '@utils/common';
import { formatAmountInDollars, formatUSDCurrency } from '@utils/amount';
import { showGenericErrorToast } from '@utils/error';
import displayToast from '@components/toast/customToast';
import useTimeZone from '@hooks/useTimeZone';

import { RoundCreateForm, RoundType, MarkupType } from '../types';
import { AddRoundProps } from '../types';
import {
  useAddRoundMutation,
  useAddValuationMutation,
  useUpdateRoundMutation,
  useUpdateValuationMutation
} from '../api';
import {
  formatDataForRoundCreation,
  formatDataForValuation
} from '../services/valuation';

const AddRoundModal: FC<AddRoundProps> = ({
  handleCloseModal,
  isOpen,
  valuationType,
  isRoundDetailsRequired = false,
  roundDetails,
  existingRoundNames,
  isOpportunity,
  dealId,
  shouldShowDealMarkup = false
}) => {
  const [createRound, { isLoading: isCreateRoundLoading }] =
    useAddRoundMutation();
  const [addValuation, { isLoading: addValuationLoading }] =
    useAddValuationMutation();
  const [updateRound, { isLoading: updateRoundLoading }] =
    useUpdateRoundMutation();
  const [updateValuation, { isLoading: updateValuationLoading }] =
    useUpdateValuationMutation();
  const { companyId } = useParams();
  const { getUtcDateWithTimeZone } = useTimeZone();

  const testCreateRoundSchemaRequiredFields = (
    currentField: string,
    formValues: yup.TestContext<yup.AnyObject>,
    value?: string | number
  ) => {
    const otherFields = Object.keys(formValues).filter(
      item =>
        ![currentField, 'valuation', 'roundDate', 'convertibleTerms'].includes(
          item
        )
    );
    if (otherFields.length && !value) return false;
    return true;
  };

  const schema = yup.object().shape({
    roundDate: yup
      .date()
      .typeError('Expected a value of type DD/MM/YYYY')
      .required('Enter Date'),
    valuation: yup
      .string()
      .transform((val, orig) => (orig == '' ? undefined : val))
      .test(translate('company.addRound.valuationPlaceholder'), val => {
        if (valuationType !== ValuationTypes.PRICED) return true;
        return !!val;
      }),
    convertibleTerms: yup
      .string()
      .max(30, '30 characters')
      .test(translate('company.addRound.convertibleTermsPlaceholder'), val => {
        if (valuationType !== ValuationTypes.CONVERTIBLE) return true;
        return !!val?.trim();
      }),
    roundName: yup
      .string()
      .transform((val, orig) => (orig == '' ? undefined : val))
      .test({
        message: val => {
          if (isRoundDetailsRequired && !val.value?.trimEnd()) {
            return translate('company.placeholders.roundName');
          }
          if (
            !roundDetails?.id &&
            existingRoundNames?.some(
              item => item?.toLowerCase() === val.value?.toLowerCase()
            )
          ) {
            return `'${val.value}' already exists`;
          }
          return translate('company.placeholders.roundName');
        },
        test: (value, d) => {
          if (isRoundDetailsRequired && !value?.trimEnd()) {
            return false;
          } else if (
            !roundDetails?.id &&
            existingRoundNames?.some(
              item => item.toLowerCase() === value?.toLowerCase().trimEnd()
            )
          )
            return false;

          return testCreateRoundSchemaRequiredFields(d.path, d.parent, value);
        }
      }),
    size: yup
      .string()
      .transform((val, orig) => (orig == '' ? undefined : val))
      .test('', translate('company.addRound.errors.required'), (value, d) => {
        if (isRoundDetailsRequired) {
          return !!value;
        }
        return testCreateRoundSchemaRequiredFields(d.path, d.parent, value);
      }),
    companyStage: yup
      .string()
      .test('', translate('company.placeholders.companyStage'), (value, d) => {
        if (isRoundDetailsRequired) {
          return !!value;
        }
        return testCreateRoundSchemaRequiredFields(d.path, d.parent, value);
      }),
    markupType: yup.string().optional(),
    markupPercentage: yup.string().when('markupType', {
      is: (val: string) => !!val,
      then: s => s.required(translate('company.addRound.markupPercentage')),
      otherwise: s => s.optional()
    })
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const {
    control,
    trigger,
    reset,
    clearErrors,
    setValue,
    formState: { errors },
    handleSubmit
  } = methods;

  const roundFields = useWatch({
    name: ['roundName', 'size', 'companyStage'],
    control
  });

  const formValidFields = useWatch({
    name: ['roundDate', 'valuation', 'convertibleTerms'],
    control
  });

  const selectedMarkupType = useWatch({
    name: 'markupType',
    control
  });

  const companyStageDropDownItems = [
    {
      id: 'EarlyStage',
      name: 'Early stage'
    },
    {
      id: 'GrowthStage',
      name: 'Growth stage'
    },
    {
      id: 'Unicorn',
      name: 'Unicorns'
    }
  ];

  const markupTypeItems = [
    {
      id: MarkupType.UPVALUATION,
      name: translate('company.addRound.markupTypes.upvaluation')
    },
    {
      id: MarkupType.DOWNVALUATION,
      name: translate('company.addRound.markupTypes.downvaluation')
    },
    {
      id: MarkupType.EXIT_GAIN,
      name: translate('company.addRound.markupTypes.exitGain')
    },
    {
      id: MarkupType.EXIT_LOSS,
      name: translate('company.addRound.markupTypes.exitLoss')
    },
    {
      id: MarkupType.SHUTDOWN,
      name: translate('company.addRound.markupTypes.shutdown')
    }
  ];

  const markupTypeDividerIds = new Set([
    MarkupType.EXIT_GAIN,
    MarkupType.SHUTDOWN
  ]);

  const isPrimaryButtonDisabled = !(
    formValidFields[0] &&
    (formValidFields[1] || formValidFields[2])
  );

  const roundDate = useMemo(() => {
    return roundDetails?.roundDate
      ? new Date(roundDetails?.roundDate)
      : undefined;
  }, [roundDetails?.roundDate]);

  const resetForm = () => {
    reset({});
  };

  const onClickCancel = () => {
    resetForm();
    handleCloseModal();
  };

  type ValidFieldNames =
    | 'roundDate'
    | 'valuation'
    | 'convertibleTerms'
    | 'size'
    | 'companyStage'
    | 'roundName';

  const onBlurAmountField = (name: ValidFieldNames, amount: string) => {
    setValue(name, getNumbersOnly(amount));
  };

  const onChangeRoundSize = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    clearErrors('size');
    if (!(Number(getNumbersOnly(event.target.value)) > ALLOCATION_MAX_AMOUNT))
      setValue(
        'size',
        formatUSDCurrency(Number(getNumbersOnly(event.target.value)))
      );
  };

  const onChangeValuation = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    clearErrors('valuation');
    if (!(Number(getNumbersOnly(event.target.value)) > ALLOCATION_MAX_AMOUNT))
      setValue(
        'valuation',
        formatUSDCurrency(Number(getNumbersOnly(event.target.value)))
      );
  };

  const onclickAddRound = async (data: RoundCreateForm) => {
    if (!roundDetails) {
      // create mode
      if (data.roundName) {
        try {
          const res = await createRound({
            company_id: companyId ?? '',
            payload: formatDataForRoundCreation(
              data,
              valuationType,
              getUtcDateWithTimeZone,
              dealId
            )
          }).unwrap();
          if (res.result === 'SUCCESS') {
            displayToast(ToastTypes.SUCCESS, 'Successfully created new round');
          } else if (res.error) {
            displayToast(
              ToastTypes.ERROR,
              res.error?.details?.[0] ?? 'Failed to create new round'
            );
          }
        } catch (err) {
          showGenericErrorToast(err);
        }
        handleCloseModal();
        resetForm();
      } else {
        try {
          const res = await addValuation({
            company_id: companyId ?? '',
            payload: formatDataForValuation(data, getUtcDateWithTimeZone)
          }).unwrap();
          if (res.result === 'SUCCESS') {
            displayToast(
              ToastTypes.SUCCESS,
              'Successfully created new valuation'
            );
          } else if (res.error) {
            displayToast(
              ToastTypes.ERROR,
              res.error?.details?.[0] ?? 'Failed to create new valuation'
            );
          }
        } catch (err) {
          showGenericErrorToast(err);
        }
        handleCloseModal();
        resetForm();
      }
    } else {
      if (roundDetails.type === RoundType.VALUATION_UPDATE) {
        try {
          const res = await updateValuation({
            company_id: companyId ?? '',
            payload: formatDataForValuation(data, getUtcDateWithTimeZone),
            valuation_id: roundDetails.id
          }).unwrap();
          if (res.result === 'SUCCESS') {
            displayToast(ToastTypes.SUCCESS, 'Successfully updated  valuation');
          } else if (res.error) {
            displayToast(
              ToastTypes.ERROR,
              res.error?.details?.[0] ?? 'Failed to update  valuation'
            );
          }
        } catch (err) {
          showGenericErrorToast(err);
        }
        handleCloseModal();
        resetForm();
      } else {
        try {
          const res = await updateRound({
            company_id: companyId ?? '',
            payload: formatDataForRoundCreation(
              data,
              valuationType,
              getUtcDateWithTimeZone,
              dealId
            ),
            round_id: roundDetails.id
          }).unwrap();
          if (res.result === 'SUCCESS') {
            displayToast(ToastTypes.SUCCESS, 'Successfully updated  round');
          } else if (res.error) {
            displayToast(
              ToastTypes.ERROR,
              res.error?.details?.[0] ?? 'Failed to  edit round'
            );
          }
        } catch (err) {
          showGenericErrorToast(err);
        }
        handleCloseModal();
        resetForm();
      }
    }
  };

  useHotkeys(
    'enter',
    () => {
      handleSubmit(onclickAddRound)();
    },
    {
      enabled: isOpen && !isPrimaryButtonDisabled
    }
  );
  useEffect(() => {
    if (roundFields.filter(item => item !== undefined && !item).length) {
      trigger(['roundName', 'size', 'companyStage']);
    }
  }, [roundFields]);

  useEffect(() => {
    if (roundDetails?.type === RoundType.VALUATION_UPDATE) {
      reset({
        roundDate: new Date(roundDetails.roundDate),
        valuation: roundDetails.valuation as string
      });
    } else {
      reset({
        roundDate: roundDetails?.roundDate
          ? new Date(roundDetails?.roundDate)
          : undefined,
        valuation: roundDetails?.valuation as string,
        companyStage: roundDetails?.companyStage,
        convertibleTerms: roundDetails?.convertibleTerms ?? '',
        roundName: roundDetails?.roundName,
        size: roundDetails?.size as string,
        markupType:
          (dealId &&
            roundDetails?.dealMarkups?.find(m => m.deal_id === dealId)
              ?.markup_type) ??
          '',
        markupPercentage: (() => {
          const markup = dealId
            ? roundDetails?.dealMarkups?.find(m => m.deal_id === dealId)
            : undefined;
          return markup?.markup_percentage
            ? String(markup.markup_percentage)
            : '';
        })()
      });
    }
  }, [roundDetails]);

  return (
    <Modal open={isOpen} onClose={handleCloseModal}>
      <div className="flex flex-col justify-center items-center h-full bg-black/60">
        <Paper
          style={{
            paddingTop: 20,
            paddingBottom: 20,
            width: 450,
            maxHeight: 822,
            overflow: 'auto'
          }}>
          <div className="flex flex-col bg-white">
            <div className="px-5 pb-5 mb-5 text-lg font-medium leading-6 border-b-[1px]">
              {translate('company.addRound.title')}
            </div>
            {!isRoundDetailsRequired && (
              <div className="px-5 pb-5 text-base font-normal text-black text-opacity-60">
                <span>Please fill the mandatory fields below to add a </span>
                <span className="font-bold">valuation update</span>
                <span> only.</span>
              </div>
            )}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onclickAddRound)}>
                <div className="px-5 pb-5">
                  <div className="border-b border-dotted">
                    <DatePicker
                      name="roundDate"
                      control={control}
                      wrapperClass="mb-5 w-full"
                      placeholder={translate('company.addRound.date')}
                      errors={errors}
                      defaultValue={roundDate}
                      autoFocusField={true}
                    />
                    {valuationType === ValuationTypes.PRICED && (
                      <RhfTextField
                        name="valuation"
                        label={
                          isOpportunity
                            ? translate(
                                'opportunities.roundDetails.valuationLabel'
                              )
                            : translate('company.addRound.valuation')
                        }
                        placeholder={
                          isOpportunity
                            ? translate(
                                'opportunities.roundDetails.valuationPlaceholder'
                              )
                            : translate('company.placeholders.valuation')
                        }
                        control={control}
                        wrapperClass="mb-5 w-full"
                        onBlur={e =>
                          onBlurAmountField('valuation', e.target.value)
                        }
                        onChange={onChangeValuation}
                        errors={errors}
                        description={
                          isOpportunity
                            ? translate(
                                'opportunities.roundDetails.valuationDescription'
                              )
                            : translate('company.description.valuation')
                        }
                        onClickTextField={() => {}}
                        showCurrency
                        isAmountField
                        formatNumberFn={formatAmountInDollars}
                      />
                    )}
                    {valuationType === ValuationTypes.CONVERTIBLE && (
                      <RhfTextField
                        name="convertibleTerms"
                        label={
                          isOpportunity
                            ? translate(
                                'opportunities.roundDetails.convertibleTerms'
                              )
                            : translate('company.addRound.convertibleTerms')
                        }
                        placeholder={
                          isOpportunity
                            ? translate(
                                'opportunities.roundDetails.convertibleTermsPlaceholder'
                              )
                            : translate(
                                'company.addRound.convertibleTermsPlaceholder'
                              )
                        }
                        control={control}
                        wrapperClass="mb-5 w-full"
                        errors={errors}
                        maxLength={CONVERTIBLE_TERMS_MAX_LENGTH}
                        description={
                          isOpportunity
                            ? translate(
                                'opportunities.roundDetails.convertibleTermsDescription'
                              )
                            : translate('company.description.convertibleTerms')
                        }
                        showCharacterLimit
                      />
                    )}
                  </div>
                  {!isRoundDetailsRequired && (
                    // eslint-disable-next-line tailwindcss/no-custom-classname
                    <p className="py-5 text-base text-black  border-gray-200 text-opacity-60 ">
                      {translate('company.addRound.message')}
                    </p>
                  )}

                  <RhfTextField
                    name="roundName"
                    label={translateDynamic('company.addRound.name', {
                      isRequired: isRoundDetailsRequired ? '*' : ' '
                    })}
                    placeholder={translateDynamic(
                      'company.placeholders.roundName'
                    )}
                    onChange={e => {
                      clearErrors('roundName');
                      setValue('roundName', e.target.value);
                    }}
                    control={control}
                    wrapperClass="mb-5 w-full"
                    errors={errors}
                    isDisabled={
                      roundDetails?.type === RoundType.VALUATION_UPDATE
                    }
                    // onChange={onTextFieldChange}
                    description={translate('company.description.roundName')}
                    showCharacterLimit
                  />

                  <RhfTextField
                    name="size"
                    label={
                      isOpportunity
                        ? translate(
                            'opportunities.roundDetails.totalRoundSizeLabel'
                          )
                        : translateDynamic('company.addRound.size', {
                            isRequired: isRoundDetailsRequired ? '*' : ' '
                          })
                    }
                    placeholder={
                      isOpportunity
                        ? translate(
                            'opportunities.roundDetails.totalRoundSizePlaceholder'
                          )
                        : translateDynamic(
                            'company.placeholders.totalRoundSize'
                          )
                    }
                    control={control}
                    wrapperClass="mb-5 w-full"
                    errors={errors}
                    isDisabled={
                      roundDetails?.type === RoundType.VALUATION_UPDATE
                    }
                    onBlur={e => onBlurAmountField('size', e.target.value)}
                    onChange={onChangeRoundSize}
                    // onChange={onTextFieldChange}
                    description={
                      isOpportunity
                        ? translate(
                            'opportunities.roundDetails.totalRoundSizePlaceholder'
                          )
                        : translate('company.description.totalRoundSize')
                    }
                    onClickTextField={() => {}}
                    showCurrency
                    formatNumberFn={formatAmountInDollars}
                    isAmountField
                  />

                  <RhfDropdown
                    control={control}
                    name="companyStage"
                    dropdownLOV={companyStageDropDownItems}
                    placeholder={translateDynamic('company.addRound.stage', {
                      isRequired: isRoundDetailsRequired ? '*' : ' '
                    })}
                    wrapperClass="mb-5 w-full"
                    errors={errors}
                    isDisabled={
                      roundDetails?.type === RoundType.VALUATION_UPDATE
                    }
                    selectedItem={
                      companyStageDropDownItems.find(
                        item => item.id === roundDetails?.companyStage
                      )?.id ?? ''
                    }
                    onChangeHandler={() => clearErrors('companyStage')}
                    description={
                      isOpportunity
                        ? translate(
                            'opportunities.roundDetails.companyStageDescription'
                          )
                        : translate('company.description.roundCompanyStage')
                    }
                  />

                  {dealId && shouldShowDealMarkup && (
                    <div className="pt-5 border-t border-gray-300 border-dotted">
                      <div className="flex flex-col gap-4 mb-5">
                        <span className="text-lg font-medium leading-6 text-black/[0.87]">
                          {translate('company.addRound.portfolioUpdateDetails')}
                        </span>
                        <p className="text-base text-[#FF5252]">
                          {translate(
                            'company.addRound.portfolioUpdateDescription'
                          )}
                        </p>
                      </div>

                      <RhfDropdown
                        control={control}
                        name="markupType"
                        dropdownLOV={markupTypeItems}
                        placeholder={translate('company.addRound.markupType')}
                        wrapperClass="mb-5 w-full"
                        errors={errors}
                        selectedItem={
                          markupTypeItems.find(
                            item =>
                              item.id ===
                              roundDetails?.dealMarkups?.find(
                                m => m.deal_id === dealId
                              )?.markup_type
                          )?.id ?? ''
                        }
                        onChangeHandler={() => clearErrors('markupType')}
                        getCustomMenuItem={option => (
                          <div
                            style={{
                              paddingTop: 8,
                              paddingBottom: 8,
                              ...(markupTypeDividerIds.has(
                                option.id as MarkupType
                              ) && {
                                borderTop: '1px dashed #C4C4C4',
                                marginLeft: -16,
                                marginRight: -16,
                                marginTop: -6,
                                paddingLeft: 16,
                                paddingRight: 16,
                                paddingTop: 14
                              })
                            }}>
                            {option.name}
                          </div>
                        )}
                      />

                      <div
                        style={{
                          opacity: selectedMarkupType ? 1 : 0.5
                        }}>
                        <RhfTextField
                          name="markupPercentage"
                          label={translate('company.addRound.markupPercentage')}
                          placeholder={translate(
                            'company.addRound.markupPercentage'
                          )}
                          control={control}
                          wrapperClass="mb-1 w-full"
                          errors={errors}
                          isDisabled={!selectedMarkupType}
                          description={translate(
                            'company.addRound.markupPercentageHelper'
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end px-5 pt-5 text-lg font-medium leading-6 border-t border-gray-200">
                  <DualButton
                    primaryButtonLabel={translate('company.addRound.save')}
                    secondaryButtonVariant="text"
                    secondaryButtonLabel={translate(
                      'company.addAngelInvestor.cancel'
                    )}
                    secondaryButtonCustomStyle={{
                      color: colors.primaryBlue
                    }}
                    //   onChangePrimaryButton={onclickAddRound}
                    onChangeSecondaryButton={onClickCancel}
                    primaryButtonType="submit"
                    isPrimaryDisabled={isPrimaryButtonDisabled}
                    isPrimaryButtonLoading={
                      isCreateRoundLoading ||
                      addValuationLoading ||
                      updateRoundLoading ||
                      updateValuationLoading
                    }
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        </Paper>
      </div>
    </Modal>
  );
};

export default AddRoundModal;
